import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Card, Text } from 'react-native-paper'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useDeleteLocation, useLocation, useUpdateLocation } from '@/hooks/locations'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { createDefaultLocationFormData } from '@/lib/locations'
import { notify } from '@/lib/notice'
import type { LocationMutatableData } from '@/types/locations'
import { FormErrorSummary } from '@/ui/form'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { DeleteLocationDialog } from '@/ui/locations/DeleteLocationDialog'
import { GeofenceLimitWarning } from '@/ui/locations/GeofenceLimitWarning'
import { LocationFormFields } from '@/ui/locations/LocationFormFields'
import { Page } from '@/ui/Page'

export default function EditLocationPage() {
    useAvoidSoftInputForScreen()
    const router = useRouter()
    const { navigateBackTo } = useNavigateBack()
    const semanticColors = useSemanticColors()
    const { locationId, orgSlug, jobSlug } = useLocalSearchParams<{
        orgSlug: string
        jobSlug: string
        locationId: string
    }>()
    const { location, isPending } = useLocation(locationId || '')
    const { updateLocation } = useUpdateLocation({
        onSuccess() {
            navigateBackTo(`/${orgSlug}/settings/jobs/${jobSlug}`)
        },
    })
    const { mutation: deleteMutation } = useDeleteLocation()

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, errors, isSubmitted, isDirty },
        reset,
    } = useForm<LocationMutatableData>({
        defaultValues: createDefaultLocationFormData(),
    })

    useEffect(() => {
        if (location) {
            reset({
                id: location.id,
                name: location.name,
                description: location.description,
                geometry: location.geometry,
                inactive: location.inactive,
            })
        }
    }, [location, reset])

    const onSubmit = handleSubmit(async data => {
        await updateLocation(data)
        reset(data)
    })

    const handleDelete = useCallback(() => {
        setDeleteError(null)
        setDeleteDialogOpen(true)
    }, [])

    const handleConfirmDelete = async () => {
        if (!location) return
        setIsDeleting(true)
        try {
            setDeleteError(null)
            await deleteMutation.mutateAsync(location.id)

            setDeleteDialogOpen(false)
            notify.success('Location deleted successfully')
            router.replace(`/${orgSlug}/settings/jobs/${jobSlug}`)
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete location'
            const isLastLocation = message.toLowerCase().includes('last location')
            setDeleteError(isLastLocation ? 'Cannot delete the last location for a job.' : message)
        } finally {
            setIsDeleting(false)
        }
    }

    useHeaderOptions({
        buttons: [
            {
                type: 'delete',
                onPress: handleDelete,
                disabled: isSubmitting || isDeleting,
                loading: isDeleting,
                loadingTitle: 'Deleting...',
            },
            {
                type: 'save',
                onPress: onSubmit,
                loading: isSubmitting,
                loadingTitle: 'Saving...',
            },
        ],
        title: location ? `Edit ${location.name || 'Location'}` : undefined,
        smallScreenTitle: 'Edit',
        enabled: !!location,
    })

    if (isPending) {
        return <LoadingIndicator message="Loading location..." />
    }

    if (!location) {
        if (isDeleting) return <LoadingIndicator message="Deleting location..." />
        return (
            <Page>
                <Card
                    style={{
                        backgroundColor: semanticColors.errorLight,
                        borderColor: semanticColors.errorBorder,
                        borderWidth: 1,
                    }}
                >
                    <Card.Content>
                        <Text style={{ color: semanticColors.errorText }}>
                            Location not found. It may have been deleted or you don't have
                            permission to edit it.
                        </Text>
                    </Card.Content>
                </Card>
            </Page>
        )
    }

    return (
        <Page>
            <GeofenceLimitWarning orgId={location.org} />
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            <LocationFormFields control={control} />
            <UnsavedChangesDialog isDirty={isDirty} />
            <DeleteLocationDialog
                visible={isDeleteDialogOpen}
                locationName={location.name}
                onDismiss={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
                errorMessage={deleteError}
            />
        </Page>
    )
}
